import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaSave, FaArrowLeft, FaEye, FaImage, FaTimes, FaUpload, FaRobot, FaChevronDown, FaChevronUp, FaLinkedin
} from 'react-icons/fa';
import { blogsApi, linkedinApi, isAuthenticated } from '../../services/api';

const BlogEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    status: 'draft',
    featured_image: '',
    meta_title: '',
    meta_description: '',
    category: '',
    auto_post_linkedin: true,
  });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(false);

  // AI Generation states
  const [showAiGenerator, setShowAiGenerator] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [aiOptions, setAiOptions] = useState({
    topic: '',
    category: '',
    tone: 'professional',
    length: 'medium',
  });

  // LinkedIn Preview states
  const [showLinkedInModal, setShowLinkedInModal] = useState(false);
  const [linkedinContent, setLinkedinContent] = useState('');
  const [linkedinGenerating, setLinkedinGenerating] = useState(false);
  const [linkedinPosting, setLinkedinPosting] = useState(false);
  const [linkedinStatus, setLinkedinStatus] = useState({ connected: false, loading: true });
  const [linkedinError, setLinkedinError] = useState('');
  const [linkedinSuccess, setLinkedinSuccess] = useState('');

  const fetchCategories = useCallback(async () => {
    try {
      const response = await blogsApi.getCategories();
      if (response.success && response.data?.categories) {
        setCategories(response.data.categories);
      }
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  }, []);

  const fetchBlog = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch single blog by ID using admin endpoint
      const response = await blogsApi.getById(id);
      if (response.success && response.data?.blog) {
        const blog = response.data.blog;
        setFormData({
          title: blog.title || '',
          content: blog.content || '',
          excerpt: blog.excerpt || '',
          status: blog.status || 'draft',
          featured_image: blog.featured_image || '',
          meta_title: blog.meta_title || '',
          meta_description: blog.meta_description || '',
          category: blog.category || '',
          auto_post_linkedin: blog.auto_post_linkedin !== false,
        });
      } else {
        setError('Blog not found');
      }
    } catch (err) {
      setError('Failed to load blog');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const checkLinkedInStatus = useCallback(async () => {
    try {
      const response = await linkedinApi.getStatus();
      if (response.success) {
        setLinkedinStatus({ ...response.data, loading: false });
      } else {
        setLinkedinStatus({ connected: false, loading: false });
      }
    } catch (err) {
      console.error('Failed to check LinkedIn status:', err);
      setLinkedinStatus({ connected: false, loading: false });
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/admin');
      return;
    }

    fetchCategories();
    checkLinkedInStatus();

    if (isEditing) {
      fetchBlog();
    }
  }, [id, navigate, isEditing, fetchBlog, fetchCategories, checkLinkedInStatus]);

  const handleSubmit = async (e, publishStatus = null) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    const dataToSend = {
      ...formData,
      status: publishStatus || formData.status,
    };

    // Remove empty optional fields
    if (!dataToSend.meta_title) delete dataToSend.meta_title;
    if (!dataToSend.meta_description) delete dataToSend.meta_description;
    if (!dataToSend.featured_image) delete dataToSend.featured_image;

    try {
      let response;
      if (isEditing) {
        response = await blogsApi.update(parseInt(id), dataToSend);
      } else {
        response = await blogsApi.create(dataToSend);
      }

      if (response.success) {
        navigate('/admin/blogs');
      } else {
        setError(response.message || 'Failed to save blog');
      }
    } catch (err) {
      setError(err.message || 'Connection error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const response = await blogsApi.uploadImage(file);
      if (response.success) {
        setFormData(prev => ({
          ...prev,
          featured_image: response.data.url,
        }));
      } else {
        setError(response.message || 'Failed to upload image');
      }
    } catch (err) {
      setError(err.message || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = () => {
    setFormData(prev => ({
      ...prev,
      featured_image: '',
    }));
  };

  const handleAiOptionChange = (e) => {
    const { name, value } = e.target;
    setAiOptions(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleGenerateLinkedInPreview = async () => {
    if (!id) {
      setError('Please save the blog first to generate LinkedIn content');
      return;
    }

    setLinkedinGenerating(true);
    setLinkedinError('');
    setLinkedinSuccess('');

    try {
      const response = await linkedinApi.generatePreview(parseInt(id));
      if (response.success && response.data) {
        setLinkedinContent(response.data.content);
        setLinkedinError('');
        setLinkedinSuccess('');
        setShowLinkedInModal(true);
      } else {
        setLinkedinError(response.message || 'Failed to generate LinkedIn content');
      }
    } catch (err) {
      setLinkedinError(err.message || 'Failed to generate LinkedIn content');
    } finally {
      setLinkedinGenerating(false);
    }
  };

  const handlePostToLinkedIn = async () => {
    if (!linkedinContent.trim()) {
      setLinkedinError('LinkedIn content is empty');
      return;
    }

    setLinkedinPosting(true);
    setLinkedinError('');
    setLinkedinSuccess('');

    try {
      const response = await linkedinApi.post(parseInt(id), linkedinContent, true, false);
      if (response.success) {
        setLinkedinSuccess('Successfully posted to LinkedIn!');
        // Close modal after 2 seconds
        setTimeout(() => {
          setShowLinkedInModal(false);
          setLinkedinContent('');
          setLinkedinSuccess('');
        }, 2000);
      } else {
        setLinkedinError(response.message || 'Failed to post to LinkedIn');
      }
    } catch (err) {
      setLinkedinError(err.message || 'Failed to post to LinkedIn');
    } finally {
      setLinkedinPosting(false);
    }
  };

  const handleAiGenerate = async () => {
    if (!aiOptions.topic.trim()) {
      setError('Please enter a topic for the blog');
      return;
    }

    setGenerating(true);
    setError('');

    try {
      const response = await blogsApi.generate(
        aiOptions.topic,
        aiOptions.category,
        aiOptions.tone,
        aiOptions.length
      );

      if (response.success && response.data) {
        // Map AI category to our category IDs
        const categoryMap = {
          'Cyber Security': 'cyber-security',
          'ERPNext': 'erpnext',
          'Cloud Computing': 'cloud-computing',
          'Compliance': 'compliance',
          'Automation': 'automation',
          'Infrastructure': 'infrastructure',
          'Programming': 'programming',
          'General': 'general',
        };
        const mappedCategory = categoryMap[aiOptions.category] || '';

        setFormData(prev => ({
          ...prev,
          title: response.data.title || prev.title,
          excerpt: response.data.excerpt || prev.excerpt,
          content: response.data.content || prev.content,
          meta_title: response.data.meta_title || prev.meta_title,
          meta_description: response.data.meta_description || prev.meta_description,
          category: mappedCategory || prev.category,
        }));
        setShowAiGenerator(false);
        setAiOptions({ topic: '', category: '', tone: 'professional', length: 'medium' });
      } else {
        setError(response.message || 'Failed to generate blog content');
      }
    } catch (err) {
      setError(err.message || 'Failed to generate blog content');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/blogs')}
                className="flex items-center space-x-2 text-dark-500 hover:text-primary-500 transition-colors"
              >
                <FaArrowLeft />
                <span>Back</span>
              </button>
              <h1 className="text-xl font-bold text-dark-800">
                {isEditing ? 'Edit Post' : 'New Post'}
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setPreview(!preview)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                  preview
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-dark-500 hover:bg-gray-100'
                }`}
              >
                <FaEye />
                <span className="hidden sm:inline">Preview</span>
              </button>
              <button
                onClick={(e) => handleSubmit(e, 'draft')}
                disabled={saving}
                className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg text-dark-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                <FaSave />
                <span className="hidden sm:inline">Save Draft</span>
              </button>
              <button
                onClick={(e) => handleSubmit(e, 'published')}
                disabled={saving}
                className="flex items-center space-x-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
              >
                {saving ? (
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <FaSave />
                )}
                <span className="hidden sm:inline">Publish</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error Message */}
        {error && (
          <motion.div
            className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6 flex items-center justify-between"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span>{error}</span>
            <button onClick={() => setError('')}>
              <FaTimes />
            </button>
          </motion.div>
        )}

        {preview ? (
          /* Preview Mode */
          <motion.div
            className="bg-white rounded-xl shadow-sm p-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {formData.featured_image && (
              <img
                src={formData.featured_image}
                alt={formData.title}
                className="w-full h-64 object-cover rounded-lg mb-6"
              />
            )}
            <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full mb-4 ${
              formData.status === 'published'
                ? 'bg-green-100 text-green-700'
                : 'bg-yellow-100 text-yellow-700'
            }`}>
              {formData.status === 'published' ? 'Published' : 'Draft'}
            </span>
            <h1 className="text-3xl font-bold text-dark-800 mb-4">
              {formData.title || 'Untitled Post'}
            </h1>
            <p className="text-dark-500 mb-6 italic">{formData.excerpt}</p>
            <div
              className="prose prose-lg max-w-none
                prose-headings:text-dark-800 prose-headings:font-bold
                prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4 prose-h2:text-primary-700
                prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
                prose-p:text-dark-600 prose-p:leading-relaxed prose-p:mb-4
                prose-li:text-dark-600 prose-li:my-1
                prose-ul:my-4 prose-ul:pl-6
                prose-ol:my-4 prose-ol:pl-6
                prose-strong:text-dark-800 prose-strong:font-semibold
                prose-blockquote:border-l-4 prose-blockquote:border-primary-500 prose-blockquote:pl-4 prose-blockquote:py-2 prose-blockquote:my-4 prose-blockquote:bg-primary-50 prose-blockquote:italic prose-blockquote:text-dark-600 prose-blockquote:rounded-r-lg
                prose-code:bg-gray-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm
                prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:p-4 prose-pre:rounded-lg"
              dangerouslySetInnerHTML={{ __html: formData.content }}
            />
          </motion.div>
        ) : (
          /* Edit Mode */
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* AI Blog Generator */}
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl shadow-sm border border-purple-100">
              <button
                type="button"
                onClick={() => setShowAiGenerator(!showAiGenerator)}
                className="w-full px-6 py-4 flex items-center justify-between text-left"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg">
                    <FaRobot className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-dark-800">AI Blog Generator</h3>
                    <p className="text-sm text-dark-500">Generate blog content using OpenAI</p>
                  </div>
                </div>
                {showAiGenerator ? <FaChevronUp className="text-dark-400" /> : <FaChevronDown className="text-dark-400" />}
              </button>

              <AnimatePresence>
                {showAiGenerator && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6 space-y-4 border-t border-purple-100 pt-4">
                      {/* Topic Input */}
                      <div>
                        <label className="block text-sm font-medium text-dark-700 mb-2">
                          Blog Topic *
                        </label>
                        <input
                          type="text"
                          name="topic"
                          value={aiOptions.topic}
                          onChange={handleAiOptionChange}
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                          placeholder="e.g., Zero Trust Security Architecture Best Practices"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Category */}
                        <div>
                          <label className="block text-sm font-medium text-dark-700 mb-2">
                            Category
                          </label>
                          <select
                            name="category"
                            value={aiOptions.category}
                            onChange={handleAiOptionChange}
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                          >
                            <option value="">Select category...</option>
                            <option value="Cyber Security">Cyber Security</option>
                            <option value="ERPNext">ERPNext</option>
                            <option value="Cloud Computing">Cloud Computing</option>
                            <option value="Compliance">Compliance</option>
                            <option value="Automation">Automation</option>
                            <option value="Infrastructure">Infrastructure</option>
                            <option value="Programming">Programming</option>
                            <option value="General">General</option>
                          </select>
                        </div>

                        {/* Tone */}
                        <div>
                          <label className="block text-sm font-medium text-dark-700 mb-2">
                            Writing Tone
                          </label>
                          <select
                            name="tone"
                            value={aiOptions.tone}
                            onChange={handleAiOptionChange}
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                          >
                            <option value="professional">Professional</option>
                            <option value="conversational">Conversational</option>
                            <option value="technical">Technical</option>
                            <option value="beginner-friendly">Beginner Friendly</option>
                          </select>
                        </div>

                        {/* Length */}
                        <div>
                          <label className="block text-sm font-medium text-dark-700 mb-2">
                            Blog Length
                          </label>
                          <select
                            name="length"
                            value={aiOptions.length}
                            onChange={handleAiOptionChange}
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                          >
                            <option value="short">Short (500-700 words)</option>
                            <option value="medium">Medium (800-1200 words)</option>
                            <option value="long">Long (1500-2000 words)</option>
                          </select>
                        </div>
                      </div>

                      {/* Generate Button */}
                      <button
                        type="button"
                        onClick={handleAiGenerate}
                        disabled={generating || !aiOptions.topic.trim()}
                        className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg hover:from-purple-600 hover:to-blue-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {generating ? (
                          <>
                            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span>Generating with AI...</span>
                          </>
                        ) : (
                          <>
                            <FaRobot />
                            <span>Generate Blog Content</span>
                          </>
                        )}
                      </button>

                      <p className="text-xs text-dark-400 text-center">
                        Generated content will populate all fields. You can edit the content after generation.
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Title */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <label className="block text-sm font-medium text-dark-700 mb-2">
                Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="w-full px-4 py-3 text-xl font-medium border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                placeholder="Enter blog title..."
                required
              />
            </div>

            {/* Featured Image */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <label className="block text-sm font-medium text-dark-700 mb-2">
                Featured Image
              </label>

              {formData.featured_image ? (
                <div className="relative">
                  <img
                    src={formData.featured_image}
                    alt="Featured"
                    className="w-full h-48 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={removeImage}
                    className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                  >
                    <FaTimes />
                  </button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                  <FaImage className="w-12 h-12 text-dark-300 mx-auto mb-4" />
                  <p className="text-dark-500 mb-4">Upload a featured image</p>
                  <label className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors cursor-pointer">
                    {uploading ? (
                      <>
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Uploading...</span>
                      </>
                    ) : (
                      <>
                        <FaUpload />
                        <span>Choose File</span>
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={uploading}
                    />
                  </label>
                  <p className="text-xs text-dark-400 mt-2">
                    Max file size: 5MB. Formats: JPEG, PNG, GIF, WebP
                  </p>
                </div>
              )}
            </div>

            {/* Excerpt */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <label className="block text-sm font-medium text-dark-700 mb-2">
                Excerpt
                <span className="text-dark-400 font-normal ml-2">
                  (Short description for blog listing)
                </span>
              </label>
              <textarea
                name="excerpt"
                value={formData.excerpt}
                onChange={handleChange}
                rows={2}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none"
                placeholder="Brief summary of the blog post..."
              />
              <p className="text-xs text-dark-400 mt-2">
                {formData.excerpt.length}/300 characters
              </p>
            </div>

            {/* Category */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <label className="block text-sm font-medium text-dark-700 mb-2">
                Category
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
              >
                <option value="">Select a category...</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-dark-400 mt-2">
                Choose a category to help readers find your content
              </p>
            </div>

            {/* Content */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <label className="block text-sm font-medium text-dark-700 mb-2">
                Content *
              </label>
              <textarea
                name="content"
                value={formData.content}
                onChange={handleChange}
                rows={20}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none font-mono text-sm"
                placeholder="Write your blog content here...

You can use HTML tags for formatting:

<h2>Heading</h2>
<p>Paragraph text</p>
<strong>Bold text</strong>
<em>Italic text</em>
<ul><li>List item</li></ul>
<blockquote>Quote</blockquote>
<pre><code>Code block</code></pre>"
                required
              />
              <p className="text-xs text-dark-400 mt-2">
                Word count: {formData.content.split(/\s+/).filter(w => w).length}
              </p>
            </div>

            {/* SEO Settings */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-medium text-dark-800 mb-4">SEO Settings</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-dark-700 mb-2">
                    Meta Title
                    <span className="text-dark-400 font-normal ml-2">(optional)</span>
                  </label>
                  <input
                    type="text"
                    name="meta_title"
                    value={formData.meta_title}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                    placeholder="SEO title (defaults to blog title)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-700 mb-2">
                    Meta Description
                    <span className="text-dark-400 font-normal ml-2">(optional)</span>
                  </label>
                  <textarea
                    name="meta_description"
                    value={formData.meta_description}
                    onChange={handleChange}
                    rows={2}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none"
                    placeholder="SEO description (defaults to excerpt)"
                  />
                </div>
              </div>
            </div>

            {/* Status */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <label className="block text-sm font-medium text-dark-700 mb-4">
                Status
              </label>
              <div className="flex items-center space-x-4">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="status"
                    value="draft"
                    checked={formData.status === 'draft'}
                    onChange={handleChange}
                    className="w-4 h-4 text-primary-500 border-gray-300 focus:ring-primary-500"
                  />
                  <span className="text-dark-700">Draft</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="status"
                    value="published"
                    checked={formData.status === 'published'}
                    onChange={handleChange}
                    className="w-4 h-4 text-primary-500 border-gray-300 focus:ring-primary-500"
                  />
                  <span className="text-dark-700">Published</span>
                </label>
              </div>
            </div>

            {/* LinkedIn Auto-Post Setting */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FaLinkedin className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-dark-700">Auto-Post to LinkedIn</h3>
                    <p className="text-xs text-dark-400">
                      Automatically share this blog on LinkedIn when published
                    </p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="auto_post_linkedin"
                    checked={formData.auto_post_linkedin}
                    onChange={(e) => setFormData(prev => ({ ...prev, auto_post_linkedin: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Manual LinkedIn Post Button */}
              {isEditing && linkedinStatus.connected && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={handleGenerateLinkedInPreview}
                    disabled={linkedinGenerating}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {linkedinGenerating ? (
                      <>
                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <FaLinkedin className="w-4 h-4" />
                        <span>Generate & Preview LinkedIn Post</span>
                      </>
                    )}
                  </button>
                  <p className="text-xs text-dark-400 mt-2">
                    Generate AI-powered LinkedIn content, preview and edit before posting
                  </p>
                </div>
              )}

              {isEditing && !linkedinStatus.connected && !linkedinStatus.loading && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-sm text-dark-500">
                    <a href="/admin/settings" className="text-blue-600 hover:underline">
                      Connect LinkedIn
                    </a> to manually post this blog
                  </p>
                </div>
              )}
            </div>
          </form>
        )}

        {/* LinkedIn Preview Modal */}
        <AnimatePresence>
          {showLinkedInModal && (
            <motion.div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowLinkedInModal(false)}
            >
              <motion.div
                className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <FaLinkedin className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-dark-800">LinkedIn Post Preview</h3>
                      <p className="text-sm text-dark-500">Edit and post to your LinkedIn</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowLinkedInModal(false)}
                    className="p-2 text-dark-400 hover:text-dark-600 transition-colors"
                  >
                    <FaTimes className="w-5 h-5" />
                  </button>
                </div>

                {/* Modal Body */}
                <div className="p-6 overflow-y-auto max-h-[60vh]">
                  <label className="block text-sm font-medium text-dark-700 mb-2">
                    Post Content
                    <span className="text-dark-400 font-normal ml-2">
                      ({linkedinContent.length}/3000 characters)
                    </span>
                  </label>
                  <textarea
                    value={linkedinContent}
                    onChange={(e) => setLinkedinContent(e.target.value)}
                    rows={15}
                    maxLength={3000}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none text-sm"
                    placeholder="Your LinkedIn post content..."
                  />
                  <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                    <p className="text-xs text-blue-700">
                      <strong>Tips:</strong> LinkedIn posts perform best at 800-1500 characters.
                      Use emojis sparingly and include relevant hashtags at the end.
                    </p>
                  </div>

                  {/* Error Message */}
                  {linkedinError && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start justify-between">
                      <p className="text-sm text-red-700">{linkedinError}</p>
                      <button
                        onClick={() => setLinkedinError('')}
                        className="ml-2 text-red-500 hover:text-red-700"
                      >
                        <FaTimes className="w-4 h-4" />
                      </button>
                    </div>
                  )}

                  {/* Success Message */}
                  {linkedinSuccess && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-700 flex items-center space-x-2">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>{linkedinSuccess}</span>
                      </p>
                    </div>
                  )}
                </div>

                {/* Modal Footer */}
                <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
                  <button
                    onClick={handleGenerateLinkedInPreview}
                    disabled={linkedinGenerating}
                    className="flex items-center space-x-2 px-4 py-2 text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50"
                  >
                    {linkedinGenerating ? (
                      <>
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Regenerating...</span>
                      </>
                    ) : (
                      <>
                        <FaRobot className="w-4 h-4" />
                        <span>Regenerate</span>
                      </>
                    )}
                  </button>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => setShowLinkedInModal(false)}
                      className="px-4 py-2 text-dark-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handlePostToLinkedIn}
                      disabled={linkedinPosting || !linkedinContent.trim()}
                      className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      {linkedinPosting ? (
                        <>
                          <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span>Posting...</span>
                        </>
                      ) : (
                        <>
                          <FaLinkedin className="w-4 h-4" />
                          <span>Post to LinkedIn</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default BlogEditor;
